import React, { useMemo, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Card, Page } from 'components';
import * as Roles from 'constants/roles';
import { useAuthStore } from 'stores/authStore';
import useSelectedRetailerProfile from 'hooks/useSelectedRetailerProfile';
import PasswordChangeModal from './PasswordChangeModal';

export const UserProfileContent = () => {
  const { t } = useTranslation(['app']);
  const user = useAuthStore((state) => state.user);
  const role = useAuthStore((state) => state.role);
  const selectedRetailerProfile = useSelectedRetailerProfile();
  const isRetailer = role === Roles.RETAILER;
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const extractRegion = () =>
    isRetailer
      ? selectedRetailerProfile?.region?.name
      : user?.admin_details?.brand?.regions.map((r) => r.name).join(', ');

  const extractBrand = () =>
    isRetailer ? selectedRetailerProfile?.brand?.name : user?.admin_details?.brand?.name;

  const getData = () => {
    const data = [
      {
        label: t('app:name'),
        value: user.name,
      },
      {
        label: t('app:last_name'),
        value: user.last_name,
      },
      {
        label: t('app:email'),
        value: user.email,
      },
      {
        label: t('app:entities.region'),
        value: extractRegion(),
      },
      {
        label: t('app:entities.brand'),
        value: extractBrand(),
      },
      ...(isRetailer
        ? [
            {
              label: t('app:retailerStore'),
              value: selectedRetailerProfile?.public_url,
              isLink: true,
            },
          ]
        : []),
    ];

    return data;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const profileData = useMemo(() => getData(), [user, role, selectedRetailerProfile]);

  return (
    <Container className="px-5 mt-5 mx-auto">
      <Card title={t('app:profileTitle')} subtitle={t('app:profileSubtitle')}>
        <ul className="list-group">
          {profileData.map((item) => (
            <li className="list-group-item" key={item.label}>
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="m-0 w-50">{item.label}</h4>
                {item?.isLink ? (
                  <Button
                    variant="link"
                    href={item.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`m-0 text-right ${item.value?.length < 50 ? 'w-50' : 'w-100'}`}
                  >
                    {item.value}
                  </Button>
                ) : (
                  <p className={`m-0 text-right ${item.value?.length < 50 ? 'w-50' : 'w-75'}`}>
                    {item.value}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-3 d-flex justify-content-end">
          <Button variant="primary" onClick={() => setIsPasswordModalOpen(true)}>
            {t('app:changePassword').toUpperCase()}
          </Button>
        </div>
      </Card>
      {isPasswordModalOpen && (
        <PasswordChangeModal setIsPasswordModalOpen={setIsPasswordModalOpen} />
      )}
    </Container>
  );
};

const UserProfile = () => {
  const { t } = useTranslation(['app']);

  return (
    <Page title={t('profileTitle')} description={t('profileDescription')} withBackButton={false}>
      <UserProfileContent />
    </Page>
  );
};

export default UserProfile;
